import { useState, useEffect, useMemo } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToEmpleados,
  subscribeToServicios,
  subscribeToPagosNomina,
  subscribeToPagosDirectos,
  addPagoNomina,
  empleadoEnServicio,
  Empleado,
  Servicio,
  PagoNomina,
  PagoDirecto,
} from "../../firebase/firebaseService";
import {
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPrint,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaDollarSign,
  FaHome,
} from "react-icons/fa";

// ─── Helpers ───────────────────────────────────────────────────────────────
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const getQuincenaKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const q = date.getDate() <= 15 ? "1" : "2";
  return `${y}-${m}-${q}`;
};

const getQuincenaLabel = (key: string): string => {
  const [y, m, q] = key.split("-");
  const mes = MESES[parseInt(m) - 1];
  return q === "1" ? `1–15 de ${mes} ${y}` : `16–31 de ${mes} ${y}`;
};

const getQuincenaEndDate = (key: string): Date => {
  const [y, m, q] = key.split("-").map(Number);
  if (q === 1) return new Date(y, m - 1, 15, 23, 59, 59);
  return new Date(y, m, 0, 23, 59, 59);
};

const getPastQuincenas = (n: number): string[] => {
  const list: string[] = [];
  let y = new Date().getFullYear();
  let mo = new Date().getMonth() + 1;
  let q = new Date().getDate() <= 15 ? 1 : 2;
  for (let i = 0; i < n; i++) {
    if (q === 1) { q = 2; mo--; if (mo === 0) { mo = 12; y--; } }
    else q = 1;
    list.push(`${y}-${String(mo).padStart(2,"0")}-${q}`);
  }
  return list;
};

const formatDate = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

const formatTime = (d: Date) =>
  d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

// ─── PDF Generator ─────────────────────────────────────────────────────────
const generarPDFUnificado = (
  empleado: Empleado,
  servicios: Servicio[],
  pagosDirectos: PagoDirecto[],
  quincena: string,
  totalPagado: number,
  deudasAnteriores: { quincena: string; monto: number }[] = []
) => {
  const win = window.open("", "_blank");
  if (!win) return;

  const today = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  const totalHoras = servicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);

  const serviciosSection = servicios.length > 0 ? `
    <h3 style="font-size:13px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.05em;margin-bottom:12px">Servicios de Limpieza</h3>
    <div class="grid" style="margin-bottom:16px">
      <div class="card"><label>Tarifa por Hora</label><span>$${empleado.costoPorHora.toFixed(2)} / hora</span></div>
      <div class="card"><label>Total Horas</label><span>${totalHoras.toFixed(2)} horas</span></div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Fecha</th><th>Casa / Cliente</th>
          <th style="text-align:center">Horario</th>
          <th style="text-align:center">Horas</th>
          <th style="text-align:center">Tarifa</th>
          <th style="text-align:right">Subtotal</th>
        </tr>
      </thead>
      <tbody>
        ${servicios.map(sv => `<tr>
          <td>${formatDate(sv.fechaServicio)}</td>
          <td>${sv.casaNombre}</td>
          <td style="text-align:center">${formatTime(sv.fechaHoraInicio)} – ${formatTime(sv.fechaHoraFin)}</td>
          <td style="text-align:center">${sv.horasTrabajadas}h</td>
          <td style="text-align:center">$${empleado.costoPorHora.toFixed(2)}</td>
          <td style="text-align:right;font-weight:600">$${(sv.horasTrabajadas * empleado.costoPorHora).toFixed(2)}</td>
        </tr>`).join("")}
      </tbody>
    </table>
  ` : "";

  const pagosDirectosSection = pagosDirectos.length > 0 ? `
    <h3 style="font-size:13px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.05em;margin-bottom:12px;${servicios.length > 0 ? "margin-top:8px" : ""}">Pagos Directos</h3>
    <table>
      <thead>
        <tr>
          <th>Fecha</th><th>Concepto</th><th style="text-align:right">Monto</th>
        </tr>
      </thead>
      <tbody>
        ${pagosDirectos.map(pd => `<tr>
          <td>${formatDate(pd.fechaPago)}</td>
          <td>${pd.concepto}</td>
          <td style="text-align:right;font-weight:600">$${pd.monto.toFixed(2)}</td>
        </tr>`).join("")}
      </tbody>
    </table>
  ` : "";

  const deudasHtml = deudasAnteriores.length > 0
    ? `<div class="aviso-deuda">
        <strong>⚠ Deuda de quincenas anteriores:</strong>
        ${deudasAnteriores.map(d => `<span>${getQuincenaLabel(d.quincena)}: <strong>$${d.monto.toFixed(2)}</strong></span>`).join(" &nbsp;|&nbsp; ")}
        <br/>Total acumulado: <strong>$${(totalPagado + deudasAnteriores.reduce((s,d)=>s+d.monto,0)).toFixed(2)}</strong>
       </div>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Comprobante de Pago – ${empleado.nombre} ${empleado.apellidos}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'Inter',sans-serif;background:#fff;color:#1f2937;padding:40px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:24px;border-bottom:3px solid #4f46e5;margin-bottom:32px}
    .brand{font-size:26px;font-weight:800;color:#4f46e5;letter-spacing:-0.5px}
    .brand-sub{font-size:12px;color:#6b7280;margin-top:3px}
    .receipt-label{text-align:right}
    .receipt-label h2{font-size:18px;font-weight:700;color:#374151}
    .receipt-label p{font-size:12px;color:#9ca3af;margin-top:3px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:28px}
    .card{background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:14px}
    .card label{font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.06em;display:block;margin-bottom:4px}
    .card span{font-size:14px;font-weight:700;color:#1f2937}
    table{width:100%;border-collapse:collapse;margin-bottom:24px;font-size:13px}
    th{background:#4f46e5;color:#fff;padding:10px 12px;text-align:left;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
    td{padding:9px 12px;border-bottom:1px solid #f3f4f6}
    tr:nth-child(even) td{background:#f9fafb}
    .total-box{background:linear-gradient(135deg,#4f46e5,#7c3aed);color:#fff;border-radius:12px;padding:20px 24px;text-align:right;display:inline-block;float:right;min-width:240px;margin-bottom:32px}
    .total-box .label{font-size:11px;opacity:.8;text-transform:uppercase;letter-spacing:.08em}
    .total-box .amount{font-size:30px;font-weight:800;margin-top:4px}
    .clearfix::after{content:"";display:table;clear:both}
    .sigs{display:grid;grid-template-columns:1fr 1fr;gap:80px;margin-top:60px}
    .sig-line{border-top:2px solid #d1d5db;padding-top:8px;text-align:center;font-size:11px;color:#6b7280}
    .footer{margin-top:32px;text-align:center;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:16px}
    .aviso-deuda{background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;padding:12px 16px;margin-bottom:20px;font-size:12px;color:#92400e;line-height:1.8}
    @media print{body{padding:20px}.no-print{display:none!important}}
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">GOBIES CLEANING SERVICES</div>
      <div class="brand-sub">Servicios Profesionales de Limpieza</div>
    </div>
    <div class="receipt-label">
      <h2>COMPROBANTE DE PAGO</h2>
      <p>Emitido: ${today}</p>
    </div>
  </div>

  <div class="grid">
    <div class="card"><label>Empleado</label><span>${empleado.nombre} ${empleado.apellidos}</span></div>
    <div class="card"><label>Período de Pago</label><span>${getQuincenaLabel(quincena)}</span></div>
  </div>

  ${deudasHtml}
  ${serviciosSection}
  ${pagosDirectosSection}

  <div class="clearfix">
    <div class="total-box">
      <div class="label">Total a Pagar (esta quincena)</div>
      <div class="amount">$${totalPagado.toFixed(2)}</div>
    </div>
  </div>

  <div class="sigs">
    <div class="sig-line">Firma del Empleador</div>
    <div class="sig-line">Firma del Empleado · ${empleado.nombre} ${empleado.apellidos}</div>
  </div>

  <div class="footer">
    Gobies Cleaning Services &nbsp;·&nbsp; Comprobante generado el ${today} &nbsp;·&nbsp; Este documento sirve como recibo oficial de pago
  </div>

  <script>setTimeout(function(){window.print();},600);</script>
</body>
</html>`;

  win.document.write(html);
  win.document.close();
};

// ─── Component ─────────────────────────────────────────────────────────────
export default function NominaAdmin() {
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [pagosDirectos, setPagosDirectos] = useState<PagoDirecto[]>([]);
  const [pagos, setPagos] = useState<PagoNomina[]>([]);
  const [loading, setLoading] = useState(true);

  const currentQ = getQuincenaKey(new Date());
  const allTabs = [currentQ, ...getPastQuincenas(11)]; // 12 quincenas = 6 meses
  const [activeTab, setActiveTab] = useState(currentQ);

  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const u1 = subscribeToEmpleados((d) => { setEmpleados(d); setLoading(false); });
    const u2 = subscribeToServicios(setServicios);
    const u3 = subscribeToPagosNomina(setPagos);
    const u4 = subscribeToPagosDirectos(setPagosDirectos);
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  // Una fila por empleado unificando servicios + pagos directos
  type RegistroNomina = {
    emp: Empleado;
    empServicios: Servicio[];
    pagosDirectos: PagoDirecto[];
    totalHoras: number;
    montoServicios: number;
    montoPagosDirectos: number;
    monto: number;
    serviciosPagado: boolean;
    pagoServiciosExistente?: PagoNomina;
    pagosDirectosEstado: { pd: PagoDirecto; pagado: boolean; pagoExistente?: PagoNomina }[];
    pagado: boolean;
    overdue: boolean;
    concepto: string;
  };

  const empStats = useMemo(() => {
    const registros: RegistroNomina[] = [];
    const endDate = getQuincenaEndDate(activeTab);

    empleados.forEach((emp) => {
      const empServicios = servicios.filter(
        (s) => s.quincena === activeTab && empleadoEnServicio(s, emp.id!)
      );
      const empPagosDirectos = pagosDirectos.filter(
        (p) => p.quincena === activeTab && p.empleadoId === emp.id
      );

      if (empServicios.length === 0 && empPagosDirectos.length === 0) return;

      const totalHoras = empServicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);
      const montoServicios = totalHoras * (emp.costoPorHora || 0);
      const montoPagosDirectos = empPagosDirectos.reduce((s, pd) => s + pd.monto, 0);
      const monto = montoServicios + montoPagosDirectos;

      const pagoServiciosExistente = empServicios.length > 0
        ? pagos.find((p) => p.empleadoId === emp.id && p.quincena === activeTab && p.tipo === 'servicios')
        : undefined;
      const serviciosPagado = empServicios.length === 0 || !!pagoServiciosExistente;

      const pagosDirectosEstado = empPagosDirectos.map((pd) => {
        const pagoExistente = pagos.find(
          (p) => p.empleadoId === emp.id && p.quincena === activeTab && p.pagoDirectoId === pd.id
        );
        return { pd, pagado: !!pagoExistente, pagoExistente };
      });

      const pagado = serviciosPagado && pagosDirectosEstado.every((p) => p.pagado);
      const overdue = !pagado && endDate < new Date();

      const conceptoParts: string[] = [];
      if (empServicios.length > 0)
        conceptoParts.push(`${empServicios.length} servicio${empServicios.length !== 1 ? 's' : ''}`);
      if (empPagosDirectos.length > 0)
        conceptoParts.push(`${empPagosDirectos.length} pago${empPagosDirectos.length !== 1 ? 's' : ''} directo${empPagosDirectos.length !== 1 ? 's' : ''}`);

      registros.push({
        emp,
        empServicios,
        pagosDirectos: empPagosDirectos,
        totalHoras,
        montoServicios,
        montoPagosDirectos,
        monto,
        serviciosPagado,
        pagoServiciosExistente,
        pagosDirectosEstado,
        pagado,
        overdue,
        concepto: conceptoParts.join(' + '),
      });
    });

    return registros;
  }, [empleados, servicios, pagosDirectos, pagos, activeTab]);

  const handlePagar = async () => {
    if (!selectedReg) return;
    setPaying(true);
    try {
      const promises: Promise<string>[] = [];

      if (selectedReg.empServicios.length > 0 && !selectedReg.serviciosPagado) {
        promises.push(addPagoNomina({
          empleadoId: selectedReg.emp.id!,
          empleadoNombre: `${selectedReg.emp.nombre} ${selectedReg.emp.apellidos}`,
          quincena: activeTab,
          totalPagado: selectedReg.montoServicios,
          fechaPago: new Date(),
          tipo: 'servicios',
          totalHoras: selectedReg.totalHoras,
          costoPorHora: selectedReg.emp.costoPorHora,
          serviciosIds: selectedReg.empServicios.map((s) => s.id!),
        }));
      }

      for (const { pd, pagado } of selectedReg.pagosDirectosEstado) {
        if (!pagado) {
          promises.push(addPagoNomina({
            empleadoId: selectedReg.emp.id!,
            empleadoNombre: `${selectedReg.emp.nombre} ${selectedReg.emp.apellidos}`,
            quincena: activeTab,
            totalPagado: pd.monto,
            fechaPago: new Date(),
            tipo: 'pagoDirecto',
            pagoDirectoId: pd.id,
          }));
        }
      }

      await Promise.all(promises);

      generarPDFUnificado(
        selectedReg.emp,
        selectedReg.empServicios,
        selectedReg.pagosDirectos,
        activeTab,
        selectedReg.monto
      );

      setSelectedReg(null);
    } catch (err) {
      console.error(err);
    } finally {
      setPaying(false);
    }
  };

  // Total deuda global (all unpaid for selected quincena)
  const totalDeudaTab = empStats.filter((s) => !s.pagado).reduce((s, d) => s + d.monto, 0);
  const totalPagadoTab = empStats.filter((s) => s.pagado).reduce((s, d) => s + d.monto, 0);

  const [selectedReg, setSelectedReg] = useState<RegistroNomina | null>(null);

  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />

      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-8">
          <h1 className="text-3xl font-bold text-white">Nómina</h1>
          <p className="text-amber-100 text-sm mt-1">Control de pagos a empleados por quincena</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <FaDollarSign className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-amber-100 text-[13px] font-medium">Deuda pendiente</p>
                  <p className="text-white text-2xl font-bold">${totalDeudaTab.toFixed(0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <FaCheckCircle className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-amber-100 text-[13px] font-medium">Total pagado</p>
                  <p className="text-white text-2xl font-bold">${totalPagadoTab.toFixed(0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <FaExclamationTriangle className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-amber-100 text-[13px] font-medium">Pagos urgentes</p>
                  <p className="text-white text-2xl font-bold">{empStats.filter((s) => s.overdue).length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-8">
          {/* Quincena tabs */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex overflow-x-auto border-b border-gray-100 bg-gray-50">
              {allTabs.map((tab, idx) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab
                      ? "bg-white text-amber-600 border-b-2 border-amber-500"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {idx === 0 ? "Quincena Actual" : getQuincenaLabel(tab)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin w-8 h-8 border-4 border-amber-300 border-t-amber-600 rounded-full" />
              </div>
            ) : empStats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <FaFileInvoiceDollar size={40} className="mb-3 text-gray-200" />
                <p className="font-medium">Sin servicios en esta quincena</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Empleado</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Concepto</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Horas</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Monto</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {empStats.map((reg) => {
                      const hasServicios = reg.empServicios.length > 0;
                      const hasDirectos = reg.pagosDirectos.length > 0;
                      const avatarColor = reg.overdue
                        ? "bg-red-500"
                        : hasServicios && hasDirectos
                        ? "bg-gradient-to-br from-violet-500 to-indigo-500"
                        : hasServicios
                        ? "bg-gradient-to-br from-blue-400 to-cyan-500"
                        : "bg-gradient-to-br from-amber-400 to-orange-500";
                      return (
                        <tr
                          key={`${reg.emp.id}-${activeTab}`}
                          className={`transition-colors ${
                            reg.overdue ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50/60"
                          }`}
                        >
                          {/* Tipo */}
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              {hasServicios && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold w-fit">
                                  <FaHome size={9} />Servicios
                                </span>
                              )}
                              {hasDirectos && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold w-fit">
                                  <FaDollarSign size={9} />Directo
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Empleado */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${avatarColor}`}>
                                {reg.emp.nombre[0]}{reg.emp.apellidos[0]}
                              </div>
                              <div>
                                <p className={`font-semibold text-sm ${reg.overdue ? "text-red-700" : "text-gray-800"}`}>
                                  {reg.emp.nombre} {reg.emp.apellidos}
                                </p>
                                {reg.overdue && (
                                  <p className="text-[11px] text-red-500 font-bold flex items-center gap-1">
                                    <FaExclamationTriangle size={9} /> PAGAR URGENTE
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Concepto */}
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{reg.concepto}</span>
                          </td>

                          {/* Horas */}
                          <td className="px-6 py-4 hidden sm:table-cell">
                            {reg.totalHoras > 0 ? (
                              <span className="flex items-center gap-1 text-sm font-semibold text-gray-700">
                                <FaClock size={11} className="text-gray-400" />{reg.totalHoras.toFixed(1)}h
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>

                          {/* Monto */}
                          <td className="px-6 py-4">
                            <span className={`font-bold text-sm ${reg.overdue ? "text-red-600" : "text-gray-800"}`}>
                              ${reg.monto.toFixed(2)}
                            </span>
                          </td>

                          {/* Estado */}
                          <td className="px-6 py-4">
                            {reg.pagado ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                                <FaCheckCircle size={10} /> PAGADO
                              </span>
                            ) : reg.overdue ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 animate-pulse">
                                <FaExclamationTriangle size={10} /> URGENTE
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                PENDIENTE
                              </span>
                            )}
                          </td>

                          {/* Acciones */}
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => setSelectedReg(reg)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                reg.pagado
                                  ? "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                  : "bg-amber-500 text-white hover:bg-amber-600 shadow-md shadow-amber-200"
                              }`}
                            >
                              {reg.pagado ? "Ver detalle" : "Ver y Pagar"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Detalle y Pago */}
      {selectedReg && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                  selectedReg.empServicios.length > 0 && selectedReg.pagosDirectos.length > 0
                    ? 'bg-gradient-to-br from-violet-500 to-indigo-500'
                    : selectedReg.empServicios.length > 0
                    ? 'bg-gradient-to-br from-blue-400 to-cyan-500'
                    : 'bg-gradient-to-br from-amber-400 to-orange-500'
                }`}>
                  {selectedReg.emp.nombre[0]}{selectedReg.emp.apellidos[0]}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{selectedReg.emp.nombre} {selectedReg.emp.apellidos}</h2>
                  <p className="text-sm text-gray-400">{getQuincenaLabel(activeTab)} · {selectedReg.concepto}</p>
                </div>
              </div>
              <button onClick={() => setSelectedReg(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <FaTimes size={16} />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="p-6 space-y-4">

              {/* Sección servicios */}
              {selectedReg.empServicios.length > 0 && (
                <div className={`rounded-xl border ${selectedReg.serviciosPagado ? 'border-emerald-200 bg-emerald-50/50' : 'border-blue-100 bg-blue-50'}`}>
                  <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <div className="flex items-center gap-2">
                      <FaHome size={12} className="text-blue-500" />
                      <p className="text-sm text-blue-700 font-semibold">Servicios de limpieza</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-700">${selectedReg.montoServicios.toFixed(2)}</span>
                      {selectedReg.serviciosPagado && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                          <FaCheckCircle size={9} /> PAGADO
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto rounded-b-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-white/60">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Casa</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Horas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/70">
                        {selectedReg.empServicios.map((sv) => (
                          <tr key={sv.id}>
                            <td className="px-4 py-2.5 text-gray-600">{formatDate(sv.fechaServicio)}</td>
                            <td className="px-4 py-2.5 text-gray-700">{sv.casaNombre}</td>
                            <td className="px-4 py-2.5 text-right font-semibold">{sv.horasTrabajadas}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sección pagos directos */}
              {selectedReg.pagosDirectos.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FaDollarSign size={12} className="text-amber-500" />
                    <p className="text-sm text-amber-700 font-semibold">Pagos directos</p>
                  </div>
                  <div className="space-y-2">
                    {selectedReg.pagosDirectosEstado.map(({ pd, pagado }) => (
                      <div key={pd.id} className={`rounded-xl p-3 flex items-center justify-between ${pagado ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-100'}`}>
                        <div>
                          <p className="text-sm text-gray-800 font-medium">{pd.concepto}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{formatDate(pd.fechaPago)}</p>
                        </div>
                        <div className="text-right shrink-0 ml-4">
                          <p className="font-bold text-gray-800">${pd.monto.toFixed(2)}</p>
                          {pagado && (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-bold">
                              <FaCheckCircle size={9} /> PAGADO
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-end pt-2">
                <div className="rounded-xl px-6 py-4 text-right min-w-[200px] bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
                  <p className="text-xs opacity-80 uppercase tracking-wider">Total</p>
                  <p className="text-3xl font-bold mt-1">${selectedReg.monto.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 border-t border-gray-100">
              <button
                onClick={() => setSelectedReg(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
              {!selectedReg.pagado && (
                <button
                  onClick={handlePagar}
                  disabled={paying}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-60 shadow-lg shadow-amber-200 flex items-center justify-center gap-2"
                >
                  <FaPrint size={14} />
                  {paying ? "Procesando..." : "Pagar y Generar PDF"}
                </button>
              )}
              {selectedReg.pagado && (
                <button
                  onClick={() => generarPDFUnificado(selectedReg.emp, selectedReg.empServicios, selectedReg.pagosDirectos, activeTab, selectedReg.monto)}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <FaPrint size={14} /> Reimprimir PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
